<!-- 跟进记录的添加 / 编辑表单弹窗（列表页使用，客户可自行选择） -->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="55%">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="110px"
    >
      <el-row>
        <el-col :span="12">
          <el-form-item label="跟进方式" prop="type">
            <el-select v-model="formData.type" class="w-1/1" placeholder="请选择跟进方式">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_TYPE)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="跟进时间" prop="followTime">
            <el-date-picker
              v-model="formData.followTime"
              class="w-1/1"
              placeholder="请选择跟进时间"
              type="datetime"
              value-format="x"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="客户" prop="customerId">
            <el-select
              v-model="formData.customerId"
              class="w-1/1"
              :disabled="lockCustomer"
              filterable
              placeholder="请选择客户"
              @change="handleCustomerChange"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系人" prop="contactIds">
            <el-select
              v-model="formData.contactIds"
              class="w-1/1"
              collapse-tags
              collapse-tags-tooltip
              filterable
              multiple
              placeholder="请选择联系人"
            >
              <el-option
                v-for="item in contactList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="跟进状态" prop="followStatus">
            <el-select v-model="formData.followStatus" class="w-1/1" placeholder="请选择跟进状态">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_STATUS)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="下次跟进时间" prop="nextTime">
            <el-date-picker
              v-model="formData.nextTime"
              class="w-1/1"
              placeholder="请选择下次跟进时间"
              type="datetime"
              value-format="x"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="跟进内容" prop="content">
            <el-input
              v-model="formData.content"
              :rows="3"
              placeholder="请输入跟进内容"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="跟进备注" prop="remark">
            <el-input
              v-model="formData.remark"
              :rows="2"
              placeholder="请输入跟进备注"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="申请人">
            <el-input v-model="applicantName" disabled placeholder="申请人" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">提 交</el-button>
      <el-button @click="dialogVisible = false">结 束</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { FollowUpRecordApi } from '@/api/crm/followup'
import * as CustomerApi from '@/api/crm/customer'
import * as ContactApi from '@/api/crm/contact'
import { BizTypeEnum } from '@/api/crm/permission'
import { useUserStore } from '@/store/modules/user'

defineOptions({ name: 'FollowUpEditForm' })

/**
 * 是否锁定「客户」字段（禁止切换）。
 * 从「某个客户的上下文」打开时（客户列表行操作、客户详情页）传 true：
 * 客户已由上下文确定，允许改动只会把跟进记到别的客户上。
 * 「全部跟进 / 我的跟进」列表页不传（默认 false），保持用户自选客户的原行为。
 */
const props = withDefaults(defineProps<{ lockCustomer?: boolean }>(), { lockCustomer: false })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const userStore = useUserStore()

/** 默认跟进方式：微信沟通（字典 crm_follow_up_type 的 value=4） */
const DEFAULT_FOLLOW_UP_TYPE = 4

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const customerList = ref<any[]>([]) // 客户下拉
const contactList = ref<any[]>([]) // 联系人下拉（跟随所选客户）
const applicantName = ref('') // 申请人名称（只读）

/**
 * ★ 客户下拉的「兜底客户」：当前预置的客户**不在** `customerList` 里时补一条合成选项。
 *
 * 根因背景 —— `/crm/customer/simple-list` 只返回**当前登录人有读权限**的客户，
 * 且后端 `CrmCustomerMapper#selectPage` 的 else 分支会 `isNotNull(owner_user_id)` 把公海客户排除。
 * 所以「详情页带进来的客户」理论上可能不在下拉数据里；一旦 `el-select` 的选项里找不到该 id，
 * 显示层就会**回退成裸值**（把 id 直接渲染出来）。
 *
 * ⚠️ 这是**防御性兜底，不是主修复**。
 * 2026-09-18 的实测案例（客户详情 → 新增跟进记录 → 客户框显示 `56`）真因是
 * `FollowUpList.vue` 调 `openForm('create')` 时**根本没传 customerId**（见该文件的注释），
 * 与数据权限无关。本兜底只负责「传了 id 但列表里没有」这一残余场景。
 *
 * 名称优先取调用方传进来的 `customerName`（零请求、绝不失败）；没传时才按 id 查一次详情，
 * 且**失败即静默放弃**（下拉缺一项总能接受，弹窗打不开才是故障）。
 */
const fallbackCustomerId = ref<number | undefined>(undefined) // 需要兜底的客户编号
const fallbackCustomerName = ref('') // 该客户已知的名称（来自调用方，可为空）

/** 判断某个编号是否已在当前客户列表里 */
const isCustomerInList = (id: number) =>
  customerList.value.some((item: any) => item && item.id === id)

/** 把兜底客户补进客户列表（已存在则不做任何事） */
const ensureCustomerInList = () => {
  const id = fallbackCustomerId.value
  if (!id || isCustomerInList(id)) {
    return
  }
  customerList.value = [
    { id, name: fallbackCustomerName.value || `客户 #${id}` },
    ...customerList.value
  ]
}

/** 按 id 查一次客户详情，补全兜底客户的名称 */
const loadFallbackCustomer = async (id: number) => {
  if (isCustomerInList(id)) {
    return
  }
  try {
    const data = await CustomerApi.getCustomer(id)
    // await 期间可能已换了客户 / 或列表已刷出该客户 ⇒ 复查一次，避免补重复
    if (data?.id && !isCustomerInList(data.id)) {
      fallbackCustomerName.value = data.name || `客户 #${data.id}`
      ensureCustomerInList()
    }
  } catch {
    // 查不到就保留占位名，不影响提交（提交只用到 id）
  }
}

const formData = ref<any>({
  id: undefined,
  bizType: BizTypeEnum.CRM_CUSTOMER,
  customerId: undefined,
  contactIds: [],
  type: DEFAULT_FOLLOW_UP_TYPE,
  followStatus: undefined,
  followTime: undefined,
  nextTime: undefined,
  content: undefined,
  remark: undefined
})
const formRules = reactive({
  type: [{ required: true, message: '跟进方式不能为空', trigger: 'change' }],
  customerId: [{ required: true, message: '客户不能为空', trigger: 'change' }],
  content: [{ required: true, message: '跟进内容不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref

/**
 * 打开弹窗
 *
 * @param type       表单类型：create - 新增；update - 修改
 * @param id         跟进记录编号（修改时必传）
 * @param customerId 预置客户编号（新增时可选；配合 lockCustomer 用于「从某个客户进入」的场景）
 * @param customerName 预置客户的名称（可选）。
 *   ★ 有就传：调用方本来就拿着客户对象，把名称一起带进来可以省掉一次兜底详情请求，
 *   也避免该请求失败时下拉里出现「客户 #56」这种占位文案。
 */
const open = async (type: string, id?: number, customerId?: number, customerName?: string) => {
  dialogTitle.value = type === 'create' ? '新增跟进记录' : '编辑跟进记录'
  formType.value = type
  // ★ 顺序不能颠倒：`resetForm()` 内部会把 fallbackCustomerId / fallbackCustomerName 清空，
  //   所以这两个值必须在 `resetForm()` **之后**赋，否则会被立即抹掉
  //   ⇒ `ensureCustomerInList()` 读到 undefined 直接 return，兜底形同不存在。
  resetForm()
  fallbackCustomerId.value = customerId
  fallbackCustomerName.value = customerName || ''
  // 顺序约定：先把下拉数据装载好，再打开弹窗。
  // 这样 el-select 在首次渲染时「选中值」与「options」同时就位，任何版本的 Element Plus 都能正确解析 label。
  // 反之（先 dialogVisible=true 再 await 拉列表）会让 select 在 options 为空时先渲染一帧，
  // 属于不必要的时序脆弱点 —— 虽然实测 EL 2.x 多数情况下会自行纠正，但不值得依赖。
  const [customers, contacts] = await Promise.all([
    CustomerApi.getCustomerSimpleList(),
    // 新增且预置了客户时，顺带把该客户的联系人一起拉好，避免开窗后二次抖动
    customerId && !id
      ? ContactApi.getContactPageByCustomer({ pageNo: 1, pageSize: 100, customerId })
      : Promise.resolve(null)
  ])
  customerList.value = customers
  // 兜底：预置的客户不在 simple-list 里（无读权限 / 已在公海）时补一条合成选项，
  // 否则 el-select 找不到 option 会把 id 当文案显示出来。
  ensureCustomerInList()
  if (contacts) {
    contactList.value = contacts.list || []
  }
  // 新增且预置客户：在打开弹窗之前就把 customerId 写进 formData，
  // 与上面的 customerList 一起构成 el-select 的完整首帧输入。
  if (!id) {
    formData.value.followTime = Date.now()
    formData.value.type = DEFAULT_FOLLOW_UP_TYPE
    applicantName.value = userStore.getUser?.nickname || ''
    if (customerId) {
      formData.value.customerId = customerId
    }
  }
  dialogVisible.value = true
  await nextTick()
  // 修改时，加载数据
  if (id) {
    formLoading.value = true
    try {
      const data = await FollowUpRecordApi.getFollowUpRecord(id)
      formData.value = {
        id: data.id,
        bizType: data.bizType,
        customerId: data.customerId,
        contactIds: data.contactIds || [],
        type: data.type,
        followStatus: data.followStatus,
        followTime: data.followTime ? new Date(data.followTime).getTime() : undefined,
        nextTime: data.nextTime ? new Date(data.nextTime).getTime() : undefined,
        content: data.content,
        remark: data.remark
      }
      applicantName.value = data.creatorName || ''
      // ★ 编辑时同样兜底：这条跟进所属的客户也可能不在 simple-list 里。
      if (data.customerId) {
        fallbackCustomerId.value = data.customerId
        ensureCustomerInList()
        await loadFallbackCustomer(data.customerId)
        await loadContacts(data.customerId)
      }
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 切换客户时重新加载联系人 */
const handleCustomerChange = async (customerId: number) => {
  formData.value.contactIds = []
  await loadContacts(customerId)
}

/** 加载客户下的联系人 */
const loadContacts = async (customerId: number) => {
  contactList.value = []
  if (!customerId) {
    return
  }
  const data = await ContactApi.getContactPageByCustomer({
    pageNo: 1,
    pageSize: 100,
    customerId
  })
  contactList.value = data.list || []
}

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = {
      ...formData.value,
      bizType: BizTypeEnum.CRM_CUSTOMER,
      bizId: formData.value.customerId
    }
    if (formType.value === 'create') {
      await FollowUpRecordApi.createFollowUpRecord(data)
      message.success(t('common.createSuccess'))
    } else {
      await FollowUpRecordApi.updateFollowUpRecord(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    bizType: BizTypeEnum.CRM_CUSTOMER,
    customerId: undefined,
    contactIds: [],
    type: DEFAULT_FOLLOW_UP_TYPE,
    followStatus: undefined,
    followTime: undefined,
    nextTime: undefined,
    content: undefined,
    remark: undefined
  }
  contactList.value = []
  applicantName.value = ''
  fallbackCustomerId.value = undefined
  fallbackCustomerName.value = ''
  formRef.value?.resetFields()
}
</script>
